import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { StoreContext } from '@/components/providers/store.provider';
import type { AccountResponse, CallbackResponse, RefreshResponse } from './auth.types';
import { useApi } from '../api/api.service';
import type { ResponseType } from '@/lib/types/response.type';
import { errorHandler } from '@/lib/utils/error-handler';
import type { TelegramAuthData } from '@/components/login-button/types';

export const useAuth = (dto: TelegramAuthData) => {
  const { api } = useApi();
  const { userStore } = useContext(StoreContext);
  const auth = useQuery({
    queryKey: ['/auth/callback', dto],
    queryFn: async () => {
      try {
        const response = await api.get<CallbackResponse>('/auth/callback', { params: dto });
        localStorage.setItem('access-token', response.data.result.token);
        userStore.setIsAuth(true);
        userStore.setUser(response.data.result.user);
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    staleTime: 8000,
  });
  return { auth };
};

export const useRefresh = () => {
  const { api } = useApi();
  const { userStore } = useContext(StoreContext);
  const refresh = useMutation<RefreshResponse, Error>({
    mutationFn: async () => {
      try {
        const response = await api.get<RefreshResponse>('/auth/refresh');
        localStorage.setItem('access-token', response.data.result.token);
        userStore.setIsAuth(true);
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
  });
  return { refresh };
};

export const useAccount = () => {
  const { api } = useApi();
  const { userStore } = useContext(StoreContext);
  const account = useQuery<AccountResponse, Error>({
    queryKey: ['/auth/account'],
    queryFn: async () => {
      try {
        const response = await api.get<AccountResponse>('/auth/account');
        userStore.setUser(response.data.result);
        userStore.setIsAuth(true);
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    enabled: userStore.isAuth,
    staleTime: 8000,
  });
  return { account };
};

export const useLogout = () => {
  const { api } = useApi();
  const { userStore } = useContext(StoreContext);
  const logout = useMutation<ResponseType, Error>({
    mutationFn: async () => {
      try {
        const response = await api.delete<ResponseType>('/auth/logout');
        userStore.setIsAuth(false);
        userStore.deleteUser();
        localStorage.removeItem('access-token');
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
  });
  return { logout };
};
